import SharesInfo from '@Components/ViewWork/SharesInfo'
import ViewWorkSection from '@Components/ViewWork/ViewWorkSection'
import React, { useEffect, useState } from 'react'
import { ZodFormattedError } from 'zod'
import { WorkType } from '@Components/ViewWork/work-type'
import generateEditLink from '@App/helpers/generate-edit-link'
import { getPublicContacts } from '@App/api/routes/rightsholder/contact'
import { Maybe } from '@Types'
import PublisherList from '@Components/ViewWork/PublisherList'
import { RightsholderContact, RightsholderContacts } from '@Entities/rightsholder/rightsholder-contact'
import EditButton from '@Components/ViewWork/EditButton'
import { PublisherModel } from '@Entities/work/publisher-model'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'

interface PublishersSectionProps {
    publishers: OriginalPublisherModel[]
    workId: number
    rightsholderId?: number
    totalKnownShares: number
    myShares?: number
    workType?: WorkType
    editing?: boolean
    publisherErrors?: ZodFormattedError<OriginalPublisherModel[]>
}

const PublishersSection = (props: PublishersSectionProps) => {
    const { publishers, workId, rightsholderId, totalKnownShares, myShares, workType, editing, publisherErrors } = props
    const publishersEditLink = generateEditLink('publishers', workId, rightsholderId, workType)
    const [publicContacts, setPublicContacts] = useState<RightsholderContacts>({ count: 0, data: [] })

    const getPublicContactsForPublishers = async (): Promise<RightsholderContacts | undefined> => {
        const adminIpIds = publishers
            .flatMap((publisher) => publisher.administratorPublishers)
            .map((ap) => ap.topMemberIpId && +ap.topMemberIpId)
        const originalIpIds = publishers
            .filter((publisher) => !publisher.administratorPublishers.length)
            .map((op) => op.topMemberIpId && +op.topMemberIpId)
        const ipIds = [...adminIpIds, ...originalIpIds].filter((ipid): ipid is number => !!ipid)
        if (ipIds.length) {
            const response = await getPublicContacts(ipIds)
            return response.data
        }
        return undefined
    }

    const getPublicContactByIpId = (publisher: PublisherModel): Maybe<RightsholderContact> =>
        publicContacts?.data?.find((contact) => contact?.ipId === publisher.topMemberIpId)

    useEffect(() => {
        let isSubscribed = true

        if (workType === 'work' || workType === 'overclaim') {
            getPublicContactsForPublishers().then((contacts) => {
                if (isSubscribed && contacts) {
                    setPublicContacts(contacts)
                }
            })
        }

        return () => {
            isSubscribed = false
        }
    }, [])

    return (
        <ViewWorkSection
            valid={props.editing !== undefined ? !publisherErrors : undefined}
            icon="publishers"
            title="Publishers"
            count={publishers.length}
            rightData={
                <>
                    <SharesInfo totalShares={totalKnownShares} myShares={myShares} />
                    {editing && (
                        <EditButton linkTo={publishersEditLink} workType={props.workType} wizardFlowIndex={3} />
                    )}
                </>
            }
        >
            {publishers.length ? (
                <PublisherList
                    publishers={publishers}
                    totalKnownShares={totalKnownShares}
                    myShares={myShares}
                    publisherErrors={publisherErrors}
                    getPubContactByIpId={getPublicContactByIpId}
                    workType={workType}
                />
            ) : (
                <div className="missing-records">No publishers listed</div>
            )}
        </ViewWorkSection>
    )
}

export default PublishersSection
